import React, { useState } from "react";
import { Bar } from "react-chartjs-2";
import dayjs from "dayjs";
import ChartWrapper from "./ChartWrapper.jsx";
import * as Images from "./images.js";
import Modal from "./components/Modal"; // Ensure you have a basic Modal component or use any modal package

const ItemsSoldView = ({ orders, startDate, endDate }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const isSingleDay = dayjs(startDate).isSame(dayjs(endDate), "day");
  const itemData = {};

  orders.forEach((order) => {
    order.items?.forEach((item) => {
      const isBox = item.name.startsWith("(Box) ");
      const baseName = isBox ? item.name.replace("(Box) ", "") : item.name;
      const date = dayjs(order.timestamp);
      const hour = date.format("HH:00");
      const day = date.format("YYYY-MM-DD");

      if (!itemData[baseName]) {
        itemData[baseName] = {
          quantity: 0,
          total: 0,
          boxQuantity: 0,
          byHour: {},
          byDay: {},
        };
      }

      itemData[baseName].quantity += item.quantity;
      itemData[baseName].total += parseFloat(item.total || 0);
      if (isBox) {
        itemData[baseName].boxQuantity += item.quantity;
      }

      const timeUnit = isSingleDay ? hour : day;
      if (!itemData[baseName][isSingleDay ? "byHour" : "byDay"][timeUnit]) {
        itemData[baseName][isSingleDay ? "byHour" : "byDay"][timeUnit] = 0;
      }
      itemData[baseName][isSingleDay ? "byHour" : "byDay"][timeUnit] += item.quantity;
    });
  });

  const sortedItems = Object.entries(itemData).sort((a, b) => b[1].quantity - a[1].quantity);

  const selectedImage = (name) => {
    const key = name.replace(/\s|é|è|ê|ë|à|ä|â|ô|ö|û|ü|ç/gi, (char) => ({
      ' ': '',
      'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e',
      'à': 'a', 'ä': 'a', 'â': 'a',
      'ô': 'o', 'ö': 'o',
      'û': 'u', 'ü': 'u',
      'ç': 'c'
    })[char] || char);
    return Images[key] || null;
  };

  const getChartData = (item) => {
    const labels = Object.keys(isSingleDay ? itemData[item].byHour : itemData[item].byDay).sort();
    const values = labels.map((label) => (isSingleDay ? itemData[item].byHour[label] : itemData[item].byDay[label]));

    return {
      labels,
      datasets: [
        {
          label: `${item} Sold`,
          data: values,
          backgroundColor: "rgba(153, 102, 255, 0.6)",
        },
      ],
    };
  };

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4 text-center">Items Sold</h2>

      <div className="w-full overflow-x-auto max-w-full">
        <table className="table-auto w-full bg-white rounded shadow overflow-hidden">

          <thead className="bg-indigo-100 text-gray-700">
            <tr>
              <th className="py-2 px-4 text-left hidden sm:table-cell">Image</th>
              <th className="py-2 px-2 text-left">Item</th>
              <th className="py-2 px-2 text-left">Total Qty</th>
              <th className="py-2 px-2 text-left">Qty in Box</th>
              <th className="py-2 px-2 text-left">Total Sales</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(([name, data], index) => (
              <tr
                key={name}
                className="cursor-pointer hover:bg-gray-100 transition odd:bg-white even:bg-gray-50"
                onClick={() => {
                  setSelectedItem(name);
                  setShowModal(true);
                }}
              >
                <td className="py-2 px-4 hidden sm:table-cell">
                  <img
                    src={selectedImage(name)}
                    alt={name}
                    className="w-10 h-10 object-contain"
                    onError={(e) => (e.target.style.display = 'none')}
                  />
                </td>
                <td className="py-2 px-2 font-medium">{name}</td>
                <td className="py-2 px-2">{data.quantity}</td>
                <td className="py-2 px-2">{data.boxQuantity}</td>
                <td className="py-2 px-2 font-semibold">${data.total.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedItem && showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <div className="p-4">
            <h3 className="text-xl font-semibold mb-4 text-center">
              {selectedItem} – {isSingleDay ? "Hourly" : "Daily"} Breakdown
            </h3>
            <ChartWrapper>
              <Bar
                data={getChartData(selectedItem)}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                  },
                  scales: {
                    y: { beginAtZero: true },
                    x: { ticks: { autoSkip: false } },
                  },
                }}
              />
            </ChartWrapper>
            <div className="text-center mt-4">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ItemsSoldView;
